import { type AnalyticsEventPayload } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { Metric } from '@atlassian/browser-metrics/types';
import {
	ExperienceName,
	sendMessageExperienceName,
} from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import {
	isConversationChannelDataMessage,
	isFollowUpMessage,
	isScopeDeterminedMessage,
	isSpaceSourceRecommendationsMessage,
	isSSEMessage,
	isStreamActionError,
	isStreamActionPart,
	isStreamActionPrepare,
	isStreamAgenticUi,
	isStreamAnswerGenerationType,
	isStreamAnswerPart,
	isStreamAttachmentBlock,
	isStreamAUPError,
	isStreamDynamicLayoutSubAgent,
	isStreamError,
	isStreamFastModeEnabled,
	isStreamFastModeResponseAvailable,
	isStreamMemoryUpdate,
	isStreamPromptBlock,
	isStreamPromptRedaction,
	isStreamReconnectSupportPart,
	isStreamRemoveAnswerNow,
	isStreamResearchPayload,
	isStreamResponse,
	isStreamResponseBlock,
	isStreamResponseRedaction,
	isStreamTaskStatus,
	isStreamThinkDeeperTriggered,
	isStreamTodoSnapshot,
	isStreamTrace,
	type StreamAgenticUi,
	type StreamMessage,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	AnswerNowStatusType,
	CHAT_STORE_STATUS,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';

import { abortGenerateContentChildExperiences } from '../../common/utils/aifc-experience-collect/abort-generate-content-child-experiences';
import { messageLatencyMetrics } from '../../common/utils/browser-metrics/messageLatencyMetrics';
import { countFragmentWords } from '../../common/utils/content-features/countFragmentWords';
import { getTopLevelNodeTypes } from '../../common/utils/content-features/getTopLevelNodeTypes';
import { randomIntFromInterval } from '../../common/utils/id-generator';
import { StreamLifecycleState } from '../bridge-api/streaming-lifecycle/StreamLifecycleState';

import { addAgenticUiMessage } from './addAgenticUiMessage';
import { addAnswerPart } from './addAnswerPart';
import { addAnswerPartWithMarkdownDataMap } from './addAnswerPartWithMarkdownDataMap';
import { addMemorytoLatestAssistantMessage } from './addMemorytoLatestAssistantMessage';
import { addRequestIdToLatestHumanMessage } from './addRequestIdToLatestHumanMessage';
import { addSubAgentMessageToStoreAssistantMessage } from './addSubAgentMessageToStoreAssistantMessage';
import { appendCreatePageFollowUpIfNeeded } from './appendCreatePageFollowUpIfNeeded';
import { clearAnswerNowStatusForMessageIfSet } from './clearAnswerNowStatusForMessageIfSet';
import { clearTracesIfNotEmpty } from './clearTracesIfNotEmpty';
import { createBetweenChunksInactivityLogger } from './createBetweenChunksInactivityLogger';
import { createChatReconnectableStream } from './createChatReconnectableStream';
import { createStreamMarkdownMultiTagProcessor } from './createStreamMarkdownMultiTagProcessor';
import { createSyntheticCreatePageFollowUp } from './createSyntheticCreatePageFollowUp';
import { createDeadAirTracker } from './dead-air-tracker';
import { dedupeTraceMessagesWithLatest } from './dedupeTraceMessages';
import { StreamErrorException } from './error-classes/StreamErrorException';
import { StreamGeneratorError } from './error-classes/StreamGeneratorError';
import { extractStreamMeta } from './extractStreamMeta';
import { findStoreMessageIndex } from './findStoreMessageIndex';
import { getActionPartKeyForMetrics } from './getActionPartKeyForMetrics';
import { getConversation } from './getConversation';
import { getConversationTraces } from './getConversationTraces';
import { getInteractionModeExecutedFromStreamResponse } from './getInteractionModeExecutedFromStreamResponse';
import { getInvokedFromInteractionSource } from './getInvokedFromInteractionSource';
import { getLastAgentMessageID } from './getLastAgentMessageID';
import { getRequestedModeAttributes } from './getRequestedModeAttributes';
import { getSourceAgentId } from './getSourceAgentId';
import { getTraceFromStreamMessage } from './getTraceFromStreamMessage';
import { handleStagingAreaActionValidation } from './handleStagingAreaActionValidation';
import { isClientSideStreamingError } from './isClientSideStreamingError';
import { minionVerificationAnalytics } from './minion-verification-analytics';
import { processAifcActionStreamMessage } from './process-aifc-action-stream-message';
import { processResearchStreamMessage } from './processResearchStreamMessage';
import { processStreamResponseMarkdown } from './processStreamResponseMarkdown';
import { recordStreamMessage } from './recordStreamMessage';
import { replaceAnswerData } from './replaceAnswerData';
import { resetStreamResponseRecording } from './resetStreamResponseRecording';
import type { ProcessStreamParams } from './sendMessageActionTypes';
import { shouldAppendCreatePageFollowUp } from './shouldAppendCreatePageFollowUp';
import { shouldPreprocessDetailsForConversation } from './shouldPreprocessDetailsForConversation';
import type { ResearchStreamState } from './types';
import { updateHumanMessageId } from './updateHumanMessageId';
import { updateStoreAssistantMessageAnswerGenerationType } from './updateStoreAssistantMessageAnswerGenerationType';
import { updateStoreAssistantMessageAnswerNowStatus } from './updateStoreAssistantMessageAnswerNowStatus';
import { updateStoreAssistantMessageId } from './updateStoreAssistantMessageId';

type InternalStreamStats = {
	totalChunks: number;
	traceChunks: number;
	answerChunks: number;
	actionChunks: number;
	researchChunks: number;
	otherChunks: number;
	firstChunkAt?: number;
	lastChunkAt?: number;
	sumInterChunkMs: number;
	pluginsObserved: Set<string>;
};

const getStreamStats = () => {
	const stats: InternalStreamStats = {
		totalChunks: 0,
		traceChunks: 0,
		answerChunks: 0,
		actionChunks: 0,
		researchChunks: 0,
		otherChunks: 0,
		firstChunkAt: undefined,
		lastChunkAt: undefined,
		sumInterChunkMs: 0,
		pluginsObserved: new Set<string>(),
	};

	const update = (streamMessage: StreamMessage) => {
		const now = performance.now();
		stats.totalChunks += 1;
		if (stats.firstChunkAt === undefined) {
			stats.firstChunkAt = now;
		}
		if (stats.lastChunkAt !== undefined) {
			stats.sumInterChunkMs += now - stats.lastChunkAt;
		}
		stats.lastChunkAt = now;

		if (isStreamTrace(streamMessage)) {
			stats.traceChunks += 1;
		} else if (isStreamAnswerPart(streamMessage)) {
			stats.answerChunks += 1;
		} else if (isStreamResearchPayload(streamMessage)) {
			stats.researchChunks += 1;
		} else if (isStreamActionPart(streamMessage) || isStreamActionPrepare(streamMessage)) {
			// Count action-related chunks (prepare/part)
			stats.actionChunks += 1;
		} else {
			// Count unknown chunks
			stats.otherChunks += 1;
		}

		// Observe plugin names if present early (often only present on final response)
		const invocations = isStreamResponse(streamMessage)
			? streamMessage.message?.message?.plugin_invocations
			: [];
		if (Array.isArray(invocations)) {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			invocations.forEach((p: any) => p?.plugin_name && stats.pluginsObserved.add(p.plugin_name));
		}
	};

	const getStats = () => {
		const avgGapMs =
			stats.totalChunks > 1 ? Math.round(stats.sumInterChunkMs / (stats.totalChunks - 1)) : 0;
		return {
			...stats,
			pluginsObserved: Array.from(stats.pluginsObserved),
			averageGapMs: avgGapMs,
		};
	};

	return { update, getStats };
};

export const processStream = async ({
	prompt,
	generator,
	productKey,
	answerIndex,
	tmpHumanMessageId,
	tmpAssistantMessageId,
	thinkDeeperEnabled,
	fastModeEnabled,
	deepResearchEnabled,
	webSearchEnabled,
	sendMessageExperienceNameCompiled,
	traceId,
	updateCurrentConversation,
	trackingAttributes,
	conversationId,
	getState,
	promptStreamHook,
	openChat,
	openChatOnError,
	experienceTracker,
	setMessages,
	getMessages,
	onSpaceSourceRecommendations,
	processStreamResearch,
	processMessageStreamAction,
	processMessageStreamComplete,
	processMessageStreamError,
	processMessageStreamActionClassification,
	containerOnTempMessageIdReplaced,
	updateResearchMessageId,
	sendAnalyticsEvent,
	chatContextStoreActions,
	stopInteractionMetricsTimeToFirstActionPart,
	stopInteractionMetricsTimeToFirstAnswerPart,
	stopInteractionMetricsTimeToActionCompletion,
	broadcastStreamLifecycle,
	sendMessageLatencyMetrics,
	cancelStreamingMetrics,
	onTempMessageIdReplacedOverride,
	streamActionInfo,
	trackAIMAUEvent,
	messageSources,
	additionalAnalyticAttributes,
	hasLoggedInitiatedEvent,
	clearMessageResearch,
	addImplicitResearchPlan,
	resetHasReceivedAnswerPart,
	viewDeadAirBroker,
	minionAlias,
	agentId,
	agentName,
	agentIdentityAccountId,
	creationContextParams,
	storeUserMessages,
	skillSelectionSource,
	assistanceService,
	abortController,
	getIsChatMounted,
	metricsStartTime,
	onTaskStatusUpdate,
	onTodoSnapshot,
	onPromptRedacted,
	onPromptBlocked,
	onResponseRedacted,
	onResponseBlocked,
	onAttachmentBlocked,
}: ProcessStreamParams): Promise<
	| {
			hasReceivedFinalResponse: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			hasAbortedForImplicitThinkDeeper?: undefined;
			implicitThinkDeeperSendMessageMetrics?: undefined;
			implicitThinkDeeperExperienceName?: undefined;
	  }
	| {
			hasReceivedFinalResponse: boolean;
			hasLoggedInitiatedEvent: boolean;
			guardEventReceived: boolean;
			hasAbortedForImplicitThinkDeeper: boolean;
			implicitThinkDeeperSendMessageMetrics: Metric | null;
			implicitThinkDeeperExperienceName: string | null;
	  }
> => {
	updateCurrentConversation({
		traces: [],
	});

	// Set to true when a guard event (PROMPT_REDACTION or PROMPT_BLOCKED) is received.
	// The backend always follows these events with an ERROR event to terminate the stream,
	// which should be treated as a successful end of the guard flow, not a real error.
	let guardEventReceived = false;

	let hasReceivedFinalResponse = false;
	let hasReceivedFollowUp = false;
	let hasReceivedActionComplete = false;
	let hasReceivedActionClassification = false;
	let isLuminaAnswerGenerationType = false;
	let agentMessageId: string | undefined;
	let isReconnectSupported = false;
	// Guards against repeated metric switches if multiple REASONING_PART messages arrive.
	let hasAbortedForImplicitThinkDeeper = false;
	// Think-deeper metrics started when implicit think-deeper is detected mid-stream. Null otherwise.
	let implicitThinkDeeperSendMessageMetrics: ReturnType<
		typeof messageLatencyMetrics.createSendMessageMetrics
	> | null = null;
	let implicitThinkDeeperTimeToFirstAnswerPartMetrics: ReturnType<
		typeof messageLatencyMetrics.createTimeToFirstAnswerPartMetrics
	> | null = null;
	// The experience name for the reliability SLO when implicit think-deeper is detected. Null otherwise.
	let implicitThinkDeeperExperienceName: string | null = null;

	// Client-side implicit round tracking for think deeper.
	// Passed by reference into processResearchStreamMessage so state is shared.
	const researchState: ResearchStreamState = {
		clientRoundCounter: 0,
		hasAccumulatedAnswerContent: false,
	};

	// Gate the details-tag preprocessor to 3P-agent conversations only.
	// Lazily initialised on the first chunk: the conversation's agent metadata
	// is set by `createConversationOptimistically` which may run after this
	// function is defined but before the first chunk arrives.
	let processStreamMarkdownMultiTagInstance: ReturnType<
		typeof createStreamMarkdownMultiTagProcessor
	> | null = null;
	const getShouldPreprocessDetails = () =>
		shouldPreprocessDetailsForConversation(getState(), conversationId);
	const getProcessStreamMarkdownMultiTag = () => {
		if (!processStreamMarkdownMultiTagInstance) {
			processStreamMarkdownMultiTagInstance = createStreamMarkdownMultiTagProcessor({
				shouldPreprocessDetails: getShouldPreprocessDetails(),
			});
		}
		return processStreamMarkdownMultiTagInstance;
	};

	let latestStreamMessage: StreamMessage | undefined;

	const streamStats = getStreamStats();
	// Between-chunks inactivity watchdog (logs once per stall, re-arms on chunks)
	const inactivity = createBetweenChunksInactivityLogger({
		baseMs: 90_000,
		sendAnalyticsEvent: (event: AnalyticsEventPayload) => {
			sendAnalyticsEvent({
				...event,
				attributes: {
					...event.attributes,
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
				},
			});
		},
	});

	// Dead air tracking - monitors gaps between meaningful stream messages
	const deadAirTracker = createDeadAirTracker({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		deepResearchEnabled: deepResearchEnabled ?? false,
		thinkDeeperEnabled: thinkDeeperEnabled ?? false,
		fastModeEnabled: fastModeEnabled ?? false,
		webSearchEnabled: webSearchEnabled ?? false,
		isAIFC: streamActionInfo.isAction,
		sendAnalyticsEvent,
	});

	viewDeadAirBroker?.start({
		messageId: tmpAssistantMessageId,
		conversationId,
		traceId: traceId ?? '',
		productKey: productKey ?? '',
		reasoningMode: deepResearchEnabled
			? 'deep-research'
			: thinkDeeperEnabled
				? 'think-deeper'
				: fastModeEnabled
					? 'fast'
					: 'auto',
		isAIFCEnabled: streamActionInfo.isAction,
		isWebSearchEnabled: webSearchEnabled ?? false,
	});

	// Reset the stream response recording for a new stream session.
	// Recording is always enabled so that network responses can be captured by default.
	const canRecordStreamResponses = true;

	if (canRecordStreamResponses) {
		resetStreamResponseRecording();
	}

	// Arm the inactivity watchdog
	inactivity?.arm();

	/*****************************
	 *
	 * Stream processing START
	 *
	 *****************************/

	const streamToIterate = createChatReconnectableStream({
		generator,
		conversationId,
		abortController,
		assistanceService,
		sendAnalyticsEvent,
		experienceTracker,
		getMessageId: () => agentMessageId,
		canReconnect: () => !!(agentMessageId && isReconnectSupported),
		updateCurrentConversation,
		getIsChatMounted,
		analyticsAttributes: {
			isDeepResearch: deepResearchEnabled,
			thinkDeeperEnabled,
			fastModeEnabled,
		},
		onBeforeFirstReconnect: () => {
			const chat = getMessages(conversationId);
			const alreadySwapped = findStoreMessageIndex(chat, agentMessageId!) >= 0;

			if (!alreadySwapped) {
				setMessages(
					conversationId,
					updateStoreAssistantMessageId({
						chat,
						oldId: tmpAssistantMessageId,
						newId: agentMessageId!,
					}),
				);
				updateResearchMessageId({
					messageId: tmpAssistantMessageId,
					newMessageId: agentMessageId!,
				});
			}

			tmpAssistantMessageId = agentMessageId!;
		},
		onReconnected: () => {
			inactivity?.arm();
		},
		onFirstChunkAfterReconnect: () => {
			deadAirTracker?.resetTimestamp();
		},
	});

	try {
		for await (const streamObj of streamToIterate) {
			const streamMessage = isSSEMessage(streamObj) ? streamObj.data : streamObj;

			// Record the raw stream message for debugging purposes
			// This allows developers to copy/paste SSE responses for examples/tests
			if (canRecordStreamResponses) {
				recordStreamMessage(streamMessage);
			}

			latestStreamMessage = streamMessage;
			deadAirTracker?.update(streamMessage);
			streamStats.update(streamMessage);
			// any chunk = proof of life
			inactivity?.onChunk();

			// Note: We do NOT skip stream messages during SWITCHING.
			// Stream processing continues normally until FAST_MODE_ENABLED is received,
			// at which point research steps are cleared and status is set to SWITCHED.

			// Usually the first stream part
			if (isConversationChannelDataMessage(streamMessage)) {
				agentMessageId = streamMessage.assistant_message_id;
				const streamConversationName = streamMessage.name?.trim();

				updateCurrentConversation({
					...(streamConversationName ? { name: streamConversationName } : {}),
					lastMessageTimestamp: streamMessage.last_message_timestamp ?? new Date().toISOString(),
					timeCreated: streamMessage.time_created,
					...(streamMessage.scopes && { scopes: streamMessage.scopes }),
				});

				trackingAttributes.instrumentationId = agentMessageId;
				trackingAttributes.messageId = agentMessageId;

				if (
					agentMessageId &&
					agentMessageId !== tmpAssistantMessageId &&
					fg('cc-cwr-fix-finalizing-cancelled-request')
				) {
					const oldTmpId = tmpAssistantMessageId;
					setMessages(
						conversationId,
						updateStoreAssistantMessageId({
							chat: getMessages(conversationId),
							oldId: oldTmpId,
							newId: agentMessageId,
						}),
					);
					updateResearchMessageId({
						messageId: oldTmpId,
						newMessageId: agentMessageId,
					});
					tmpAssistantMessageId = agentMessageId;
					(onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced)?.(
						oldTmpId,
						agentMessageId,
					);
				}

				trackingAttributes.invokedFrom =
					creationContextParams?.source === 'remix'
						? 'remix'
						: getInvokedFromInteractionSource(messageSources?.interactionSource);

				const remixNodeTypes = creationContextParams?.dynamicUiSource
					? getTopLevelNodeTypes(
							creationContextParams.dynamicUiSource.fullAdf,
							creationContextParams.dynamicUiSource.startLocalId ?? '',
							creationContextParams.dynamicUiSource.endLocalId ?? '',
						)
					: undefined;
				const remixWordCount = creationContextParams?.dynamicUiSource?.fragmentAdf
					? countFragmentWords(creationContextParams.dynamicUiSource.fragmentAdf)
					: undefined;
				if (remixNodeTypes) {
					trackingAttributes.remixNodeTypes = remixNodeTypes;
				}
				if (remixWordCount !== undefined) {
					trackingAttributes.remixWordCount = remixWordCount;
				}

				trackAIMAUEvent({
					type: 'initiated',
					subjectId: messageSources?.interactionSource,
					proactive: false,
					userGenerated: true,
					timestamp: streamMessage.last_message_timestamp,
					singleInstrumentationID: agentMessageId,
					deepResearchEnabled: deepResearchEnabled ?? false,
					thinkDeeperEnabled: thinkDeeperEnabled ?? false,
					fastModeEnabled: fastModeEnabled ?? false,
					webSearchEnabled: webSearchEnabled ?? false,
					pageContext: trackingAttributes.pageContext,
					viewContext: trackingAttributes.viewContext,
					invokedFrom: trackingAttributes.invokedFrom,
					attributes: {
						...getRequestedModeAttributes(trackingAttributes),
						...(additionalAnalyticAttributes && additionalAnalyticAttributes),
						...(remixNodeTypes !== undefined && { remixNodeTypes }),
						...(remixWordCount !== undefined && { remixWordCount }),
					},
				});
				hasLoggedInitiatedEvent = true;
			} else if (isScopeDeterminedMessage(streamMessage)) {
				updateCurrentConversation({ scopes: streamMessage.scopes });
			} else if (isSpaceSourceRecommendationsMessage(streamMessage)) {
				// No analytics event here by design: this stream message only forwards
				// recommendations to the host via a generic callback. Surfacing/interaction
				// analytics are owned by the recommendations banner UI that consumes them.
				onSpaceSourceRecommendations?.(
					streamMessage.message.space_id,
					streamMessage.message.recommendations,
				);
			} else {
				const conversation = getState().conversations[getState().currentConversationId];
				if (conversation) {
					if (conversation.status !== CHAT_STORE_STATUS.typingResponse) {
						updateCurrentConversation({
							status: CHAT_STORE_STATUS.typingResponse,
						});
					}
				}
			}

			if (isStreamReconnectSupportPart(streamMessage)) {
				isReconnectSupported = true;
			}

			if (isStreamAnswerGenerationType(streamMessage) && fg('rovo_chat_enable_lumina')) {
				isLuminaAnswerGenerationType = streamMessage.message.answer_generation_type === 'LUMINA';
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerGenerationType(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage.message.answer_generation_type,
					),
				);
			}

			{
				const { deepResearchInvoked } = processResearchStreamMessage({
					streamMessage,
					tmpAssistantMessageId,
					conversationId,
					deepResearchEnabled,
					getMessages,
					setMessages,
					processStreamResearch,
					addImplicitResearchPlan,
					resetHasReceivedAnswerPart,
					researchState,
				});
				if (deepResearchInvoked) {
					trackingAttributes.deepResearchInvoked = true;
				}
			}

			// The backend autonomously triggered thinking mode (implicit think-deeper) even though
			// the user did not explicitly enable it. Cancel the auto-mode browser metrics so these
			// slow requests don't count against the <16s/<30s SLOs, and re-attribute them to the
			// think-deeper metrics instead.
			if (
				!thinkDeeperEnabled &&
				!hasAbortedForImplicitThinkDeeper &&
				isStreamThinkDeeperTriggered(streamMessage)
			) {
				hasAbortedForImplicitThinkDeeper = true;
				// Cancel auto-mode metrics so they are not recorded in the SLOs
				stopInteractionMetricsTimeToFirstAnswerPart('cancel');
				sendMessageLatencyMetrics.cancel();
				// Re-attribute to think-deeper metrics (100s SLO target)
				implicitThinkDeeperSendMessageMetrics = messageLatencyMetrics.createSendMessageMetrics({
					thinkDeeperEnabled: true,
				});
				implicitThinkDeeperTimeToFirstAnswerPartMetrics =
					messageLatencyMetrics.createTimeToFirstAnswerPartMetrics({
						thinkDeeperEnabled: true,
					});
				implicitThinkDeeperSendMessageMetrics.start({ startTime: metricsStartTime });
				implicitThinkDeeperTimeToFirstAnswerPartMetrics.start({ startTime: metricsStartTime });
				// Abort the auto-mode reliability SLO experience and start the think-deeper one
				// so implicit think-deeper requests don't count against the auto-mode reliability SLO.
				implicitThinkDeeperExperienceName = sendMessageExperienceName({
					thinkDeeperEnabled: true,
				});
				experienceTracker.abort({
					name: sendMessageExperienceNameCompiled,
					reason: 'Re-attributed to think-deeper reliability SLO',
				});
				experienceTracker.start({
					name: implicitThinkDeeperExperienceName,
					id: conversationId,
					startTime: metricsStartTime,
					attributes: {
						conversationId,
						interactionSource: trackingAttributes.interactionSource,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
					},
				});
			}

			// This is the first moment we have the network request id for E2E event tracking
			// Store requestId on human message for E2E event tracking
			if (!trackingAttributes.requestId && 'metadata' in streamMessage) {
				trackingAttributes.requestId = streamMessage.metadata?.request_id ?? undefined;
			}

			if (isStreamResponse(streamMessage)) {
				trackingAttributes.interactionModeExecuted =
					getInteractionModeExecutedFromStreamResponse(streamMessage);
			}

			// Handle MEMORY_UPDATE trace messages - create a persistent StoreMemoryUpdate message
			if (
				isStreamMemoryUpdate(streamMessage) &&
				// eslint-disable-next-line @atlaskit/platform/use-recommended-utils -- This Statsig experiment is not registered in platform feature flags/editor experiments.
				FeatureGates.getExperimentValue('memory_management_ux', 'isEnabled', false)
			) {
				const originalContent = streamMessage.message.content;
				const isMemoryTraceContentEmpty = !originalContent;
				let memoryContent = originalContent;
				let isMemoryContentAfterParsingEmpty = false;

				// Parse memory content: remove "Update memory:" prefix and trim
				if (memoryContent) {
					memoryContent = memoryContent.replace(/^update\s+memory:\s*/i, '').trim();
					isMemoryContentAfterParsingEmpty = !memoryContent;
				}

				const memoryUpdateMessage = {
					type: 'MEMORY_UPDATE' as const,
					content: memoryContent || undefined, // Use undefined instead of empty string
				};

				// Track memory update parsing events
				sendAnalyticsEvent({
					eventType: 'track',
					action: 'parsed',
					actionSubject: 'memoryUpdate',
					attributes: {
						isMemoryTraceContentEmpty,
						isMemoryContentAfterParsingEmpty,
						conversationId,
					},
				});

				setMessages(
					conversationId,
					addMemorytoLatestAssistantMessage({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						memory: memoryUpdateMessage,
						useIndex: answerIndex,
					}),
				);
			}

			// Usually the second stream part, before anything else occurs
			if (isStreamTrace(streamMessage)) {
				if (!deepResearchEnabled) {
					// we do not want traces that are not marked for deep research to be accumulated when starting research,
					// the ones that are marked are processed above in processResearchStreamMessage
					updateCurrentConversation({
						traces: dedupeTraceMessagesWithLatest([
							...getConversationTraces(getState(), conversationId),
							getTraceFromStreamMessage(streamMessage),
						]),
					});
				}

				promptStreamHook?.onTraceMessage?.(streamMessage, tmpAssistantMessageId, openChat);

				setMessages(
					conversationId,
					addRequestIdToLatestHumanMessage(
						getMessages(conversationId),
						trackingAttributes.requestId,
					),
				);
			}

			// Handle Answer Now fast mode traces for extended thinking
			// Answer Now feature - handle fast mode traces
			if (isStreamFastModeResponseAvailable(streamMessage) && agentMessageId) {
				// Fast mode response is available - show Answer Now button
				// Store both IDs in AVAILABLE status:
				// - tmpMessageId: for store operations (finding/updating the message)
				// - remoteMessageId: for the Answer Now API call
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{
							type: AnswerNowStatusType.AVAILABLE,
							tmpMessageId: tmpAssistantMessageId,
							remoteMessageId: agentMessageId,
						},
					),
				);
			}

			if (isStreamFastModeEnabled(streamMessage)) {
				// Fast mode has been enabled:
				// 1. Set status to SWITCHED (hides button)
				// 2. Clear research steps for this message
				setMessages(
					conversationId,
					updateStoreAssistantMessageAnswerNowStatus(
						getMessages(conversationId),
						tmpAssistantMessageId,
						{ type: AnswerNowStatusType.SWITCHED },
					),
				);
				// Clear research steps when switching to fast mode
				clearMessageResearch({ messageId: tmpAssistantMessageId });
			}

			if (isStreamRemoveAnswerNow(streamMessage)) {
				clearAnswerNowStatusForMessageIfSet(
					conversationId,
					tmpAssistantMessageId,
					getMessages,
					setMessages,
				);
			}

			// isStreamResearchPlan is handled by processResearchStreamMessage (called earlier in this loop)

			const aifcActionResult = processAifcActionStreamMessage({
				streamMessage,
				conversationId,
				tmpAssistantMessageId,
				traceId,
				productKey,
				deepResearchEnabled,
				storeUserMessages,
				streamActionInfo,
				trackingAttributes,
				experienceTracker,
				chatContextStoreActions,
				getMessages,
				setMessages,
				sendAnalyticsEvent,
				getIsChatMounted,
				processMessageStreamAction,
				processMessageStreamComplete,
				processMessageStreamActionClassification,
				promptStreamHook,
				openChat,
				messageSources,
				creationContextParams,
				agentId,
				agentName,
				agentIdentityAccountId,
				stopInteractionMetricsTimeToFirstActionPart,
			});
			hasReceivedActionClassification ||= aifcActionResult.hasReceivedActionClassification;
			hasReceivedActionComplete ||= aifcActionResult.hasReceivedActionComplete;

			if (isStreamAnswerPart(streamMessage)) {
				stopInteractionMetricsTimeToFirstAnswerPart('stop', {
					answerType: 'stream-answer-part',
					conversationId,
				});
				// Stop the implicit think-deeper time-to-first-answer-part metric if active
				implicitThinkDeeperTimeToFirstAnswerPartMetrics?.stop({
					customData: { answerType: 'stream-answer-part', conversationId },
				});

				// Clear Answer Now status when ANSWER_PART is received
				// This handles the case where the stream starts answering without user clicking Answer Now
				clearAnswerNowStatusForMessageIfSet(
					conversationId,
					tmpAssistantMessageId,
					getMessages,
					setMessages,
				);

				// Sometimes an ANSWER_PART arrives with empty content, which clears traces
				// prematurely and makes the live "thinking" steps flash and disappear. Skip the
				// clear for empty answer parts so traces persist until real content arrives.
				const isEmptyAnswerPart =
					typeof streamMessage.message.content === 'string' &&
					streamMessage.message.content.length === 0;
				const skipClearForEmptyAnswerPart =
					fg('rovo_chat_skip_trace_clear_on_empty_answer_part') && isEmptyAnswerPart;
				if (!skipClearForEmptyAnswerPart) {
					clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
					// Mark hasReceivedAnswerPart on the chat store message atomically with the
					// trace clear. This avoids a cross-store render gap (chat store vs research
					// store) where the thinking indicator would briefly re-appear.
					setMessages(
						conversationId,
						(getMessages(conversationId) ?? []).map((msg) => {
							if (
								msg.type === 'ASSISTANT' &&
								msg.content.message?.message?.id === tmpAssistantMessageId
							) {
								return { ...msg, hasReceivedAnswerPart: true };
							}
							return msg;
						}),
					);
				}

				let processedStreamMessage = streamMessage;
				let hasMarkdownData = false;

				if (!isLuminaAnswerGenerationType) {
					const result = getProcessStreamMarkdownMultiTag()(processedStreamMessage);
					processedStreamMessage = result.processedStreamMessage;
					if (Object.keys(result.markdownData).length > 0) {
						hasMarkdownData = true;
						setMessages(
							conversationId,
							addAnswerPartWithMarkdownDataMap({
								chat: getMessages(conversationId),
								answerId: tmpAssistantMessageId,
								answerPart: processedStreamMessage,
								markdownData: result.markdownData,
								useIndex: answerIndex,
							}),
						);
					}
				}

				if (!isLuminaAnswerGenerationType) {
					promptStreamHook?.onAnswerPart?.(
						processedStreamMessage,
						tmpAssistantMessageId,
						conversationId,
					);
				}
				if (!hasMarkdownData) {
					setMessages(
						conversationId,
						addAnswerPart({
							chat: getMessages(conversationId),
							answerId: tmpAssistantMessageId,
							answerPart: streamMessage,
							useIndex: answerIndex,
						}),
					);
				}

				// hasAccumulatedAnswerContent tracking is handled by processResearchStreamMessage
			}

			/**********************************************************
			 *
			 * FINAL_RESPONSE processing
			 *
			 **********************************************************/
			if (isStreamResponse(streamMessage)) {
				hasReceivedFinalResponse = true;
				const humanMessage = streamMessage?.message?.human_message;
				const humanMessageId = humanMessage?.id?.toString();
				const agentMessageId = streamMessage?.message?.message?.id.toString();
				// Record the backend-assigned conversation ID on the optimistic entry so the
				// space sidenav merge logic can dedupe between the local optimistic entry
				// (client UUID) and the API-returned entry (backend ID), and display the
				// correct name from the chat store rather than the timestamp fallback.
				const backendConversationId = streamMessage?.message?.message?.conversation_channel_id;
				if (backendConversationId) {
					updateCurrentConversation({ backendConversationId });
				}
				const minionOrchestrationOutputs = streamMessage?.message?.message?.minion_outputs;
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);
				// INVARIANT: updateResearchMessageId MUST be called before replaceAnswerData (and the
				// setMessages call that wraps it). Both stores must agree on the same messageId key
				// before React re-renders — if the chat store ID flips to agentMessageId first,
				// the next render will call getMessageResearchById(agentMessageId) and get undefined
				// because the research store is still keyed under tmpAssistantMessageId, causing
				// completed traces to disappear.
				//
				// Nothing between this call and replaceAnswerData below should read the research
				// store using tmpAssistantMessageId (markdown processing and clearTracesIfNotEmpty
				// above do not touch the research store after this point).
				if (tmpAssistantMessageId !== agentMessageId) {
					updateResearchMessageId({
						messageId: tmpAssistantMessageId,
						newMessageId: agentMessageId,
					});
				}

				const { processedStreamMessage, markdownData } = !isLuminaAnswerGenerationType
					? processStreamResponseMarkdown({
							streamMessage,
							messageId: agentMessageId,
							conversationId,
							sendAnalyticsEvent,
							shouldPreprocessDetails: shouldPreprocessDetailsForConversation(
								getState(),
								conversationId,
							),
						})
					: { processedStreamMessage: streamMessage, markdownData: {} };

				// Capture whether the final answer contained Lumina content, and in
				// what form, so it can be reported on the `agentMessage received`
				// event. The two forms are mutually exclusive per message:
				//  - `answerGeneration`: the whole answer is a Lumina layout
				//    (`answer_generation_type === 'LUMINA'`), which skips the inline
				//    markdown extractor.
				//  - `inline`: Lumina blocks embedded within a markdown answer, which
				//    the extractor stores under `lumina-*` keys in `markdownData`.
				// Joined (on messageId) against the `luminaComponent shown` render
				// event to detect Lumina that was returned by BE but never rendered.
				const inlineLuminaCount = Object.keys(markdownData).filter((key) =>
					key.startsWith('lumina-'),
				).length;
				if (isLuminaAnswerGenerationType) {
					trackingAttributes.hasLumina = true;
					trackingAttributes.luminaType = 'answerGeneration';
					trackingAttributes.luminaCount = 1;
				} else if (inlineLuminaCount > 0) {
					trackingAttributes.hasLumina = true;
					trackingAttributes.luminaType = 'inline';
					trackingAttributes.luminaCount = inlineLuminaCount;
				}

				// Finish view dead air tracking before rendering final response
				// This ensures the last tracked interval is up to FINAL_RESPONSE arrival,
				// and any DOM updates from replaceAnswerData are not tracked
				viewDeadAirBroker?.finish('completed');

				promptStreamHook?.onFinalResponse?.(processedStreamMessage, tmpAssistantMessageId);
				setMessages(
					conversationId,
					replaceAnswerData({
						chat: getMessages(conversationId),
						answerId: tmpAssistantMessageId,
						content: processedStreamMessage,
						useIndex: answerIndex,
						humanMessageId,
						markdownData: Object.keys(markdownData).length > 0 ? markdownData : undefined,
						onMessageIdChanged: onTempMessageIdReplacedOverride || containerOnTempMessageIdReplaced,
					}),
				);

				if (humanMessageId) {
					setMessages(
						conversationId,
						updateHumanMessageId({
							chat: getMessages(conversationId),
							oldId: tmpHumanMessageId,
							newId: humanMessageId,
							parentMessageId: humanMessage?.parent_message_id,
						}),
					);
				}

				if (fg('aifc_create_enabled')) {
					const allMessages = getMessages(conversationId);
					const isValidAction = handleStagingAreaActionValidation(
						allMessages,
						conversationId,
						traceId,
						chatContextStoreActions,
						experienceTracker,
						getMessages,
						setMessages,
						sendMessageLatencyMetrics,
						cancelStreamingMetrics,
						{ llmDebugLogsEnabled: storeUserMessages },
						productKey,
						minionOrchestrationOutputs,
					);

					if (!isValidAction) {
						return { hasReceivedFinalResponse, hasLoggedInitiatedEvent, guardEventReceived };
					}

					const finalActions = processedStreamMessage.message.message.actions || [];
					if (finalActions.length > 0) {
						const firstActionData = finalActions[0]?.data as
							| { dynamicUiSubtype?: string }
							| undefined;
						stopInteractionMetricsTimeToActionCompletion('stop', {
							conversationId,
							actionType: 'final-actions',
							actionCount: finalActions.length,
							actionKeys: finalActions.map((a) => a.key),
							actionPartKey: getActionPartKeyForMetrics(
								streamActionInfo.actionKey,
								creationContextParams?.source,
								firstActionData?.dynamicUiSubtype,
							),
							interactionSource: messageSources?.interactionSource,
							remixType: creationContextParams?.dynamicUiType,
						});
					} else {
						stopInteractionMetricsTimeToActionCompletion('cancel');
					}
				}

				// Update tracking attributes from FINAL_RESPONSE using the shared extractor.
				const streamMeta = extractStreamMeta(
					streamMessage as StreamResponse,
					skillSelectionSource,
					expValEquals('cc-maui-experiment', 'isEnabled', true),
				);

				if (!hasLoggedInitiatedEvent) {
					trackingAttributes.messageId = agentMessageId;
					trackingAttributes.instrumentationId = agentMessageId;
				}
				trackingAttributes.humanMessageId = streamMeta.humanMessageId;
				trackingAttributes.hasSources = streamMeta.hasSources;
				trackingAttributes.citedProductList = streamMeta.citedProductList;
				trackingAttributes.sourceProducts = streamMeta.sourceProducts;
				trackingAttributes.messageActions = streamMeta.messageActions;
				trackingAttributes.messageDynamicUIData = streamMeta.messageDynamicUIData;
				trackingAttributes.invokedFrom =
					streamMeta.invokedFrom ?? messageSources?.invokedFrom ?? trackingAttributes.invokedFrom;
				trackingAttributes.scenarioId = streamMeta.scenarioId;
				trackingAttributes.sources = streamMeta.responseSources;
				trackingAttributes.usesJsm = streamMeta.usesJsm;
				// Keep Set type for compatibility with downstream consumers that call Array.from()
				trackingAttributes.pluginsInvoked = new Set(streamMeta.pluginsInvoked);
				trackingAttributes.skillsInvoked = streamMeta.skillsInvoked;
				trackingAttributes.executedStratusSkill = streamMeta.executedStratusSkill;

				const flatMinionOutputs = minionOrchestrationOutputs?.flat() ?? [];

				// Analytics: Verify if minion alias matches minion id
				minionVerificationAnalytics({
					minionAlias,
					agentId,
					minionsOutput: flatMinionOutputs,
					sendAnalyticsEvent,
				});
			}

			if (isStreamDynamicLayoutSubAgent(streamMessage) && fg('rovo_chat_enable_lumina')) {
				setMessages(
					conversationId,
					addSubAgentMessageToStoreAssistantMessage(
						getMessages(conversationId),
						tmpAssistantMessageId,
						streamMessage,
					),
				);
			}

			if (isStreamAgenticUi(streamObj) && fg('jira_ai_rendering_a2ui')) {
				const normalizedAgenticUiMessage: StreamAgenticUi = {
					...streamObj.data,
					type: 'AGENTIC_UI',
				};
				setMessages(
					conversationId,
					addAgenticUiMessage(
						getMessages(conversationId),
						tmpAssistantMessageId,
						normalizedAgenticUiMessage,
					),
				);
			}

			if (isFollowUpMessage(streamMessage)) {
				hasReceivedFollowUp = true;
				const messages = getMessages(conversationId);
				// Only clear traces if they are not already empty to avoid unnecessary state updates
				clearTracesIfNotEmpty(getState, conversationId, updateCurrentConversation);

				const augmentedFollowUp = appendCreatePageFollowUpIfNeeded(
					messages,
					streamMessage,
					productKey,
					chatContextStoreActions.getIsStagingArea(),
				);

				setMessages(conversationId, [
					...messages,
					{
						type: 'FOLLOW_UP',
						content: augmentedFollowUp,
						id: randomIntFromInterval(0, 1000000),
						agentMessageId: getLastAgentMessageID(messages),
						humanMessageId: trackingAttributes.humanMessageId,
					},
				]);
			}

			/**
			 * Task status processing
			 */
			if (isStreamTaskStatus(streamMessage)) {
				onTaskStatusUpdate?.(streamMessage);
			}

			/**
			 * Todo snapshot processing
			 */
			if (isStreamTodoSnapshot(streamMessage)) {
				onTodoSnapshot?.({ todos: streamMessage.message.todos });
			}

			if (isStreamActionError(streamMessage)) {
				sendMessageLatencyMetrics.cancel();
				cancelStreamingMetrics();
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.key,
					errorMessage: streamMessage?.message?.data?.message,
					traceId,
				});
				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				const actionError = new StreamErrorException(
					streamMessage.message.data?.message || 'Action execution failed',
					{
						detail: {
							message: {
								content: streamMessage?.message?.data?.message || 'Action execution failed',
								message_template: streamMessage?.message?.key,
							},
							metadata: {
								...streamMessage.metadata,
								isStagingArea: chatContextStoreActions.getIsStagingArea(),
								productKey,
							},
						},
						traceId: traceId,
					},
				);

				experienceTracker.fail({
					name: sendMessageExperienceNameCompiled,
					error: actionError,
					attributes: {
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						isDeepResearchEnabled: deepResearchEnabled,
						isThinkDeeperEnabled: thinkDeeperEnabled,
						isFastModeEnabled: fastModeEnabled,
						isWebSearchEnabled: webSearchEnabled,
						llmDebugLogsEnabled: storeUserMessages,
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
					},
				});

				experienceTracker.fail({
					name: ExperienceName.AIFC_MESSAGE,
					error: actionError,
					attributes: {
						productKey,
						isStreamAction: streamActionInfo.isAction,
						actionKey: streamActionInfo.actionKey,
						llmDebugLogsEnabled: storeUserMessages,
						featureArea: chatContextStoreActions.getIsStagingArea()
							? 'aifc-staging-area'
							: 'unknown',
						latestStreamMessageMillisOffset:
							latestStreamMessage && 'millisOffset' in latestStreamMessage
								? latestStreamMessage.millisOffset
								: undefined,
						latestStreamMessageType: latestStreamMessage?.type,
					},
				});

				if (fg('aifc-experience-timeout')) {
					const reason = streamActionInfo.isAction
						? 'action_error_while_executing_action'
						: 'action_error_without_classification';
					for (const name of [
						ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
						ExperienceName.AIFC_ALL_DATA_HANDLED,
					]) {
						experienceTracker.fail({
							name,
							error: actionError,
							attributes: {
								productKey,
								actionKey: streamActionInfo.actionKey,
								reason,
							},
						});
					}
				}

				throw actionError;
			}

			if (isStreamPromptRedaction(streamMessage)) {
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — the stream continues with the actual response after redaction.
					onPromptRedacted?.(streamMessage);
				}
			}

			if (isStreamPromptBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onPromptBlocked?.(streamMessage);
				}
			}

			if (isStreamResponseRedaction(streamMessage)) {
				const redactingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_redacting_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					redactingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					// Do NOT set guardEventReceived — stream continues after response redaction.
					onResponseRedacted?.(streamMessage);
				}
			}

			if (isStreamResponseBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onResponseBlocked?.(streamMessage);
				}
			}

			if (isStreamAttachmentBlock(streamMessage)) {
				const blockingTypes =
					(
						FeatureGates.getExperiment('rovo_chat_blocking_guard_config').value as {
							value?: string[];
						}
					)?.value ?? [];
				if (
					blockingTypes.includes(streamMessage.type) &&
					fg('rovo-guard-ui-redaction-and-blocking')
				) {
					guardEventReceived = true;
					onAttachmentBlocked?.(streamMessage);
				}
			}

			if (isStreamError(streamMessage)) {
				processMessageStreamError?.({
					actionKey: streamActionInfo.actionKey,
					errorType: streamMessage?.message?.message_template,
					errorMessage: streamMessage?.message?.content,
					traceId,
				});
				// If a guard event (PROMPT_REDACTION, PROMPT_BLOCKED, RESPONSE_BLOCKED, or ATTACHMENT_BLOCKED) was
				// already received, the backend always follows with an ERROR to terminate the stream.
				// Treat it as a normal end of the guard flow — break without throwing.
				if (guardEventReceived) {
					break;
				}

				const { message, metadata, millisOffset, type } = streamMessage;

				// AUP violations are still considered successful
				if (isStreamAUPError(streamMessage)) {
					experienceTracker.succeed({
						name: sendMessageExperienceNameCompiled,
					});
					experienceTracker.succeed({ name: ExperienceName.AIFC_MESSAGE });
				}

				sendMessageLatencyMetrics.cancel();
				deadAirTracker?.cancel();
				viewDeadAirBroker?.finish('errored');
				cancelStreamingMetrics();

				promptStreamHook?.onError?.(streamMessage, tmpAssistantMessageId, {
					openChatOnError,
					conversationId,
				});
				broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

				throw new StreamErrorException(message?.content || 'Unknown', {
					detail: {
						message,
						metadata,
						millisOffset,
						type,
					},
					traceId: traceId,
				});
			}
		}
	} catch (error: unknown) {
		/* for await catch block */
		const err = error instanceof Error ? error : new Error(String(error));

		if (err instanceof StreamErrorException) {
			throw err;
		}

		// this is for any errors that happening during processing the stream
		// e.g. BE terminates streaming suddenly
		sendMessageLatencyMetrics.cancel();
		deadAirTracker?.cancel();
		viewDeadAirBroker?.finish('aborted');

		cancelStreamingMetrics();

		if (isClientSideStreamingError(err)) {
			const isReconnectable = !!(agentMessageId && isReconnectSupported);

			sendAnalyticsEvent({
				eventType: 'track',
				action: 'failed',
				actionSubject: 'rovoChatStream',
				attributes: {
					...streamStats.getStats(),
					isDeepResearchEnabled: deepResearchEnabled,
					isThinkDeeperEnabled: thinkDeeperEnabled,
					isFastModeEnabled: fastModeEnabled,
					isWebSearchEnabled: webSearchEnabled,
					isAIFCStreaminAction: streamActionInfo.isAction,
					isReconnectable,
					conversationId,
					instrumentationId: trackingAttributes.instrumentationId,
					requestId: trackingAttributes.requestId,
					interactionSource: trackingAttributes.interactionSource,
					contentContext: chatContextStoreActions.getIsStagingArea() ? 'staging-area' : 'global',
					userAgent: navigator?.userAgent,
					error: err.message,
				},
			});
		}

		broadcastStreamLifecycle(StreamLifecycleState.ERRORED);

		// When reconnect attempt fails
		if (!deepResearchEnabled && fg('aifc-experience-timeout')) {
			const reason = streamActionInfo.isAction
				? 'stream_error_during_action'
				: 'stream_error_before_classification';
			for (const name of [
				ExperienceName.AIFC_INITIAL_DATA_RECEIVED,
				ExperienceName.AIFC_ALL_DATA_HANDLED,
			]) {
				experienceTracker.fail({
					name,
					error: err,
					attributes: {
						productKey,
						actionKey: streamActionInfo.actionKey,
						reason,
					},
				});
			}
		}

		throw new StreamGeneratorError(
			err.message ?? 'Stream generator error',
			{
				traceId: traceId,
				detail: {
					millisOffset:
						latestStreamMessage && 'millisOffset' in latestStreamMessage
							? latestStreamMessage.millisOffset
							: undefined,
					type: latestStreamMessage?.type ?? '',
				},
			},
			err,
		);
	} finally {
		inactivity?.stop();
		deadAirTracker?.complete();
		viewDeadAirBroker?.finish('completed');
	}

	/**********************************************************
	 *
	 * Stream processing END
	 *
	 **********************************************************/

	if (hasReceivedActionClassification && !hasReceivedActionComplete && hasReceivedFinalResponse) {
		// In the case ACTION_COMPLETE was never sent, it's likely that `AdfEditorMinion` early-exits with "no operations returned by LLM". So we abort the experiences rather than letting them timeout. This can potentially happen when LLM needs clarification.
		abortGenerateContentChildExperiences(
			experienceTracker,
			'stream_completed_without_action_complete',
			{
				actionKey: streamActionInfo.actionKey,
				productKey,
			},
		);
	}

	if (!hasReceivedFollowUp && hasReceivedFinalResponse) {
		const messages = getMessages(conversationId);
		if (
			shouldAppendCreatePageFollowUp(
				messages,
				productKey,
				chatContextStoreActions.getIsStagingArea(),
			)
		) {
			setMessages(conversationId, [
				...messages,
				{
					type: 'FOLLOW_UP',
					content: createSyntheticCreatePageFollowUp(),
					id: randomIntFromInterval(0, 1000000),
					agentMessageId: getLastAgentMessageID(messages),
					humanMessageId: trackingAttributes.humanMessageId,
				},
			]);
		}
	}

	updateCurrentConversation({
		sourceAgentId: getSourceAgentId(
			getMessages(conversationId),
			getConversation(getState(), conversationId),
		),
	});

	return {
		hasReceivedFinalResponse,
		hasLoggedInitiatedEvent,
		hasAbortedForImplicitThinkDeeper,
		implicitThinkDeeperSendMessageMetrics,
		implicitThinkDeeperExperienceName,
		guardEventReceived,
	};
};
